import { Suspense, useState } from 'react';

import { getCookie } from '@api/HelperAPI';
import { useDrawdownRepaymentSchedule } from '@api/lendingAPI/lapiHooks';
import { Constants } from '@constants';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { CurrencyInput, Table } from '@iwoca/orion';
import { Skeleton } from '@ui';
import { FormattedMessage } from 'react-intl';

import { PaymentPlanSchedule } from './PaymentPlanSchedule';
import { ErrorBoundary } from '../../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import { RCF_CASHLESS_OPENING_COOKIE } from '../RCFApprovedView';

export const PaymentPlanModalContent = ({
    offer,
    initialAmount,
    isInRepaymentFreePeriod
}: {
    offer: Pick<TFormalOffer, 'offer_id' | 'max_amount'>;
    initialAmount: number;
    isInRepaymentFreePeriod?: boolean;
}) => {
    const [drawdownAmount, setDrawdownAmount] = useState<number | null>(
        initialAmount
    );

    const locale = 'de-DE';

    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <h3 className="mb-s mt-s text-2xl font-bold">
                <FormattedMessage
                    id="_.rcf_approved_view.payment_plan_modal_title"
                    defaultMessage="Indicative payment plan"
                />
            </h3>
            <div className="mb-m">
                <div className="flex items-center gap-s">
                    <span className="text-m font-bold">
                        <FormattedMessage
                            id="_.rcf_approved_view.payment_plan_if_i_withdraw"
                            defaultMessage="If I withdraw"
                        />
                    </span>
                    <CurrencyInput
                        className="w-[140px]"
                        currency={{ locale, currencySymbol: true }}
                        defaultValue={initialAmount}
                        onValueChange={setDrawdownAmount}
                    />
                    <span className="text-m font-bold">
                        <FormattedMessage
                            id="_.rcf_approved_view.payment_plan_today"
                            defaultMessage="today"
                        />
                    </span>
                </div>
            </div>
            <Suspense fallback={<ScheduleSkeleton />}>
                <ScheduleContent
                    offer={offer}
                    drawdownAmount={drawdownAmount}
                    isInRepaymentFreePeriod={isInRepaymentFreePeriod}
                />
            </Suspense>
        </ErrorBoundary>
    );
};

const isValidDrawdownAmount = (amount: number | null, max: number) => {
    if (amount === null) return false;
    if (amount > max) return false;
    if (amount >= Constants.MIN_DRAW_DOWN_AMOUNT) return true;

    const isCashlessActivation =
        getCookie(RCF_CASHLESS_OPENING_COOKIE) !== 'false';
    if (isCashlessActivation && amount === 0) return true;

    return false;
};

const ScheduleContent = ({
    offer,
    drawdownAmount,
    isInRepaymentFreePeriod
}: {
    offer: Pick<TFormalOffer, 'offer_id' | 'max_amount'>;
    drawdownAmount: number | null;
    isInRepaymentFreePeriod?: boolean;
}) => {
    const maxAmount = offer.max_amount!;
    const isValid = isValidDrawdownAmount(drawdownAmount, maxAmount);

    const { data } = useDrawdownRepaymentSchedule({
        offerId: offer.offer_id!,
        drawdownAmount: drawdownAmount ?? 0,
        suspense: true,
        enabled: isValid
    });

    if (!data) return <ScheduleSkeleton />;

    return (
        <PaymentPlanSchedule
            repaymentSchedule={data}
            isInRepaymentFreePeriod={isInRepaymentFreePeriod}
        />
    );
};

const SKELETON_ROWS = 4;

const ScheduleSkeleton = () => (
    <Table.Root data-testid="payment-plan-skeleton">
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell>
                    <Skeleton className="h-[16px] w-[60px]" />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <Skeleton className="h-[16px] w-[70px]" />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <Skeleton className="h-[16px] w-[60px]" />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <Skeleton className="h-[16px] w-[100px]" />
                </Table.HeaderCell>
            </Table.Row>
        </Table.Header>
        <Table.Body>
            {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                <Table.Row key={i}>
                    <Table.Cell>
                        <Skeleton className="h-[16px] w-[80px]" />
                    </Table.Cell>
                    <Table.Cell>
                        <Skeleton className="h-[16px] w-[50px]" />
                    </Table.Cell>
                    <Table.Cell>
                        <Skeleton className="h-[16px] w-[60px]" />
                    </Table.Cell>
                    <Table.Cell>
                        <Skeleton className="h-[16px] w-[60px]" />
                    </Table.Cell>
                </Table.Row>
            ))}
        </Table.Body>
    </Table.Root>
);
